import { Button, Table, theme } from 'antd';
import { EditOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { VendorDetailResponse } from '@/types/api';
import type { VendorPaymentDataResponse } from '@/types/api/paymentTerm';
import TableContainer from '@/components/common/TableContainer';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';

interface PaymentSectionProps {
  payment: VendorPaymentDataResponse | null;
  vendor: VendorDetailResponse;
  canEdit: boolean;
  onEditClick: () => void;
  currentColorScheme: {
    headerBg: string;
    headerAccent: string;
    cardShadow: string;
    borderColor: string;
    highlightBg: string;
  };
  currentFontSizes: {
    title: number;
    subtitle: number;
    body: number;
    label: number;
    menuItem: number;
  };
}

export const PaymentSection = ({
  payment,
  vendor,
  canEdit,
  onEditClick,
  currentColorScheme: _currentColorScheme,
  currentFontSizes: _currentFontSizes,
}: PaymentSectionProps) => {
  const { t } = useTranslation('vendors');
  const { token } = theme.useToken();
  const elevation = useElevation();

  if (!payment) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `${token.paddingXL * 4}px ${token.paddingXL}px`,
          minHeight: '400px',
        }}
      >
        <InboxOutlined
          style={{
            fontSize: token.sizeXXL * 3,
            color: token.colorTextDisabled,
            marginBottom: token.marginXL,
          }}
        />
        <h3
          style={{
            fontSize: token.fontSizeHeading4,
            fontWeight: token.fontWeightStrong,
            color: token.colorTextSecondary,
            margin: 0,
          }}
        >
          {t('vendor.messages.noPaymentDataConfigured')}
        </h3>
      </div>
    );
  }

  return (
    <div>
      {/* Financial Information Header with Edit Button */}
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('vendor.sections.financialInformation')}
        </h3>
        <Button
          type="primary"
          icon={<EditOutlined />}
          disabled={!canEdit}
          onClick={onEditClick}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        >
          {t('vendor.actions.edit')}
        </Button>
      </div>

      <div className="tw-grid tw-grid-cols-3 tw-gap-6 tw-mb-6">
        {/* Payment Terms Card */}
        <div
          style={{
            backgroundColor: token.colorBgContainer,
            overflow: 'hidden',
            ...elevation.card,
          }}
        >
          <div
            style={{
              padding: '18px 24px',
              backgroundColor: token.colorFillQuaternary,
            }}
          >
            <h4
              style={{
                fontSize: token.fontSizeLG,
                fontWeight: token.fontWeightStrong,
                margin: 0,
                color: token.colorText,
                textTransform: 'none',
              }}
            >
              {t('vendor.sections.paymentTermsSection')}
            </h4>
          </div>
          <div style={{ padding: '24px' }}>
            <div className="tw-space-y-4">
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.labels.deposit')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {payment.depositPercentage != null ? `${payment.depositPercentage}%` : '-'}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.labels.cashDiscount')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {payment.cashDiscountPercentage != null ? `${payment.cashDiscountPercentage}%` : '-'}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.labels.terms')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {payment.paymentTerm?.customTermName || payment.paymentTerm?.termCode || '-'} {payment.paymentTerm?.customDueDays ? `(${payment.paymentTerm.customDueDays} ${t('vendor.misc.suffix.days')})` : ''}
                </div>
              </div>
              {/* Primary Currency */}
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.fields.primaryCurrency')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {payment.primaryCurrency || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tax Card */}
        <div
          style={{
            backgroundColor: token.colorBgContainer,
            overflow: 'hidden',
            ...elevation.card,
          }}
        >
          <div
            style={{
              padding: '18px 24px',
              backgroundColor: token.colorFillQuaternary,
            }}
          >
            <h4
              style={{
                fontSize: token.fontSizeLG,
                fontWeight: token.fontWeightStrong,
                margin: 0,
                color: token.colorText,
                textTransform: 'none',
              }}
            >
              {t('vendor.sections.taxInfo')}
            </h4>
          </div>
          <div style={{ padding: '24px' }}>
            <div className="tw-space-y-4">
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.fields.abn')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {vendor.localTaxId || '-'}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.fields.taxType')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {payment.taxType}
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.fields.taxRate')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {payment.taxRate}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Balance Card */}
        <div
          style={{
            backgroundColor: token.colorBgContainer,
            overflow: 'hidden',
            ...elevation.card,
          }}
        >
          <div
            style={{
              padding: '18px 24px',
              backgroundColor: token.colorFillQuaternary,
            }}
          >
            <h4
              style={{
                fontSize: token.fontSizeLG,
                fontWeight: token.fontWeightStrong,
                margin: 0,
                color: token.colorText,
                textTransform: 'none',
              }}
            >
              {t('vendor.sections.balance')}
            </h4>
          </div>
          <div style={{ padding: '24px' }}>
            <div className="tw-space-y-4">
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.fields.outstandingBalance')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {/* Outstanding balance not in new API spec */}
                  -
                </div>
              </div>
              <div>
                <label
                  className="tw-block"
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    marginBottom: '8px',
                  }}
                >
                  {t('vendor.fields.unbilledAmount')}
                </label>
                <div
                  style={{
                    fontSize: token.fontSize,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorText,
                  }}
                >
                  {/* Unbilled amount not in new API spec */}
                  -
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Currencies Table */}
      <h3
        style={{
          fontSize: token.fontSizeLG,
          fontWeight: token.fontWeightStrong,
          margin: 0,
          marginBottom: '16px',
        }}
      >
        {t('vendor.sections.currencies')}
      </h3>
      <TableContainer>
        <Table
          dataSource={[]} // Currencies not available in new API spec
          rowKey={(record: { currency: string }) => record.currency}
          size="small"
          pagination={{
            pageSize: 10,
          }}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: t('vendor.table.currency'),
              dataIndex: 'currency',
              key: 'currency',
            },
            {
              title: t('vendor.table.outstandingBalance'),
              dataIndex: 'outstandingBalance',
              key: 'outstandingBalance',
              render: (balance: number) => (balance ?? 0).toFixed(2),
            },
            {
              title: t('vendor.table.unbilledAmount'),
              dataIndex: 'unbilledAmount',
              key: 'unbilledAmount',
              render: (amount: number) => amount?.toFixed(2) ?? '--',
            },
          ]}
        />
      </TableContainer>
    </div>
  );
};

export default PaymentSection;
